import React, { useState, useEffect, useCallback, useMemo } from "react";
import { withRouter } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "../PreCloseLoan/PreCloseLoan.scss";
import { fetchAPIData } from "../../../service/service";
import { getSecureItem } from "../../../utilsFunctions";
import axios from "axios";
import { BaseUrl } from "../../../Environment";

const SETTLEMENT_METHODS = {
  DIRECT: "Collection method",
  BALLOON: "Balloon Method",
  EMI: "EMI Method",
};

const DECIMAL_INPUT_REGEX = /^\d*\.?\d*$/;

// Fixed: original used `method === (A || B)` which always collapses to
// `method === A` because `A || B` short-circuits on the truthy A.
const isBalanceMethod = (method) =>
  method === SETTLEMENT_METHODS.DIRECT || method === SETTLEMENT_METHODS.BALLOON;

const resolveMethodFromLoanDetails = (loanDetails) => {
  const rawMethod = loanDetails?.method;
  if (!rawMethod) return SETTLEMENT_METHODS.DIRECT;

  const method = String(rawMethod).toLowerCase();

  if (method.includes("emi")) return SETTLEMENT_METHODS.EMI;
  if (method.includes("interestonly") || method.includes("interest_only")) {
    return SETTLEMENT_METHODS.BALLOON;
  }
  return SETTLEMENT_METHODS.DIRECT;
};

function PreCloseLoan({
  open,
  loanId,
  loanCollectionId,
  balanceAmt,
  preclose,
}) {
  const history = useHistory();
  // Configure toast once, not in every render.
  if (!toast.isActive()) toast.configure();

  const [method, setMethod] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Direct/Balloon Method fields
  const [outstandingBalance, setOutstandingBalance] = useState(0);

  // EMI Method fields
  const [outstandingPrincipal, setOutstandingPrincipal] = useState(0);
  const [outstandingInterest, setOutstandingInterest] = useState(0);

  // Shared fields
  const [preClosureFee, setPreClosureFee] = useState("");
  const [waiver, setWaiver] = useState("");

  const loadLoanDetails = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);

    try {
      const loanDetailsRes = await fetchAPIData("getLoanDetailsByID", loanId);
      const loanDetails = loanDetailsRes?.data?.data;

      setMethod(resolveMethodFromLoanDetails(loanDetails));
      setOutstandingBalance(balanceAmt || loanDetails?.outstandingBalance || 0);
      setOutstandingPrincipal(loanDetails?.totalPrincipal || 0);
      setOutstandingInterest(loanDetails?.totalInterest || 0);
    } catch (error) {
      console.error("Fetch Loan Details Error:", error);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [loanId, balanceAmt]);

  // Load details each time the dialog opens (mirrors the old
  // componentDidUpdate open-transition check)
  useEffect(() => {
    if (open) {
      loadLoanDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loanId]);

  const handleFieldChange = (setter) => (e) => {
    const { value } = e.target;
    if (value === "" || DECIMAL_INPUT_REGEX.test(value)) {
      setter(value);
    }
  };

  const totalSettlement = useMemo(() => {
    const fee = parseFloat(preClosureFee) || 0;
    const waiverAmount = parseFloat(waiver) || 0;

    if (isBalanceMethod(method)) {
      const balance = parseFloat(outstandingBalance) || 0;
      return balance + fee - waiverAmount;
    }

    const principal = parseFloat(outstandingPrincipal) || 0;
    const interest = parseFloat(outstandingInterest) || 0;
    return principal + interest + fee - waiverAmount;
  }, [
    method,
    outstandingBalance,
    outstandingPrincipal,
    outstandingInterest,
    preClosureFee,
    waiver,
  ]);

  const onSubmitPreClose = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const token = getSecureItem("token");
      const loggedinUser = getSecureItem("loggedinUser");
      const headers = { Authorization: `Bearer ${token}` };

      const payload = {
        loanId,
        loanCollectionId, // the loanCollectionId of day 4 (the current pending row)
        collectionAmount: totalSettlement, // amount actually received to close the loan
        PenaltyApplied: 0, // optional, if any penalty is being collected too
        collectedDate: new Date().toISOString(),
        collectorId: loggedinUser?.userId,
        paymentMode: "Cash",
      };

      const resp = await axios.put(
        `${BaseUrl}/loanCollection/preCloseLoan`,
        payload,
        {
          headers,
        },
      );

      if (resp.data?.status) {
        preclose();
        toast.success(resp.data?.msg, {
          position: "top-center",
          autoClose: 3000,
          pauseOnHover: false,
        });
        history.push("/customer");
      }
    } catch (error) {
      console.error("PreClose Loan Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBreakdown = () => (
    <div className="breakdownWrapper">
      {isBalanceMethod(method) ? (
        <>
          <div className="breakdownRow">
            <span className="breakdownLabel">Outstanding Balance +</span>
            <input
              type="text"
              className="breakdownInput"
              value={isLoading ? "" : outstandingBalance}
              placeholder={isLoading ? "..." : "0"}
              disabled
            />
          </div>

          <div className="breakdownRow">
            <span className="breakdownLabel">Pre-closure Fee +</span>
            <input
              type="text"
              className="breakdownInput"
              placeholder="0"
              value={preClosureFee}
              disabled={isLoading}
              onChange={handleFieldChange(setPreClosureFee)}
            />
          </div>

          <div className="breakdownRow">
            <span className="breakdownLabel">Waiver -</span>
            <input
              type="text"
              className="breakdownInput"
              placeholder="0"
              value={waiver}
              disabled={isLoading}
              onChange={handleFieldChange(setWaiver)}
            />
          </div>
        </>
      ) : (
        <>
          <div className="breakdownRow">
            <span className="breakdownLabel">Outstanding Principal +</span>
            <input
              type="text"
              className="breakdownInput"
              value={isLoading ? "" : outstandingPrincipal}
              placeholder={isLoading ? "..." : "0"}
              disabled
            />
          </div>

          <div className="breakdownRow">
            <span className="breakdownLabel">Outstanding Interest +</span>
            <input
              type="text"
              className="breakdownInput"
              value={isLoading ? "" : outstandingInterest}
              placeholder={isLoading ? "..." : "0"}
              disabled
            />
          </div>

          <div className="breakdownRow">
            <span className="breakdownLabel">Pre-closure Fee +</span>
            <input
              type="text"
              className="breakdownInput"
              placeholder="0"
              value={preClosureFee}
              disabled={isLoading}
              onChange={handleFieldChange(setPreClosureFee)}
            />
          </div>

          <div className="breakdownRow">
            <span className="breakdownLabel">Waiver -</span>
            <input
              type="text"
              className="breakdownInput"
              placeholder="0"
              value={waiver}
              disabled={isLoading}
              onChange={handleFieldChange(setWaiver)}
            />
          </div>
        </>
      )}

      <div className="breakdownRow totalRow">
        <span className="breakdownLabel">Total Settlement:</span>
        <span className="totalValue">
          {isLoading ? "--" : totalSettlement.toFixed(2)}
        </span>
      </div>
    </div>
  );

  return (
    <>
      <div className="deleteuserpage">
        <Dialog open={open} className="closeCustomerDialogbox">
          <DialogTitle id="customized-dialog-title" className="TitleDiv">
            Pre Close Loan
            <div className="deleteconfirmation">
              <div className="Fonts btnSizeFont confrmmessage">
                By pre-closing the loan, you cannot revert the process.
              </div>

              <div className="methodToggle" aria-live="polite">
                {method === SETTLEMENT_METHODS.DIRECT && (
                  <span
                    className={`methodBadge active ${isLoading ? "skeleton" : ""}`}
                  >
                    Flat Interest
                  </span>
                )}

                {method === SETTLEMENT_METHODS.BALLOON && (
                  <span
                    className={`methodBadge active ${isLoading ? "skeleton" : ""}`}
                  >
                    Balloon Repayment
                  </span>
                )}

                <span
                  className={`methodBadge ${
                    method === SETTLEMENT_METHODS.EMI ? "active" : ""
                  } ${isLoading ? "skeleton" : ""}`}
                >
                  Reducing Balance (EMI)
                </span>
              </div>

              {loadError ? (
                <div className="loadErrorRow">
                  Couldn't load loan details.{" "}
                  <button
                    type="button"
                    className="retryLink"
                    onClick={loadLoanDetails}
                  >
                    Retry
                  </button>
                </div>
              ) : isLoading ? (
                <div className="loadingRow">
                  <CircularProgress size={16} thickness={5} />
                  <span>Fetching loan details…</span>
                </div>
              ) : (
                renderBreakdown()
              )}
            </div>
          </DialogTitle>

          <DialogContent className="bottomPart">
            <div className="buttonDiv">
              <Button
                className="replanbutton Fonts"
                onClick={preclose}
                disabled={isSubmitting}
              >
                Close
              </Button>

              <Button
                className="savebutton Fonts"
                onClick={onSubmitPreClose}
                disabled={isLoading || loadError || isSubmitting}
              >
                {isSubmitting ? "Processing..." : "Confirm"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}

export default withRouter(PreCloseLoan);
